import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Modal } from 'antd';
import dayjs from 'dayjs';
import { useCompanyConfig } from '@/app/oz/_shared/hooks';
import { ModalRowsSkeleton } from '@/components/profile';
import { ROUTES } from '@/constants';
import { DATE_FORMATS } from '@/utils/formUtils';

// Stored value → forms label key, as the OZ settings page shows them.
const VALUE_LABELS = {
  GROSS_TO_NET: 'ozDirGrossToNet',
  NET_TO_GROSS: 'ozDirNetToGross',
  FIXED_RSD: 'ozPvsFixedRsd',
  FIXED_EUR: 'ozPvsFixedEur',
  NBS_RATE: 'ozPvsNbsRate',
  CALENDAR: 'ozWhCalendar',
  FIXED_176: 'ozWh176',
  FIXED_168: 'ozWh168',
  CUSTOM: 'ozWhCustom',
  ROUND: 'ozRoundRound',
  FLOOR: 'ozRoundFloor',
  CEIL: 'ozRoundCeil',
  BY_PRIORITY: 'ozDeductByPriority',
  PROPORTIONAL: 'ozDeductProportional',
  GROSS: 'ozOvertimeBaseGross',
  NET: 'ozOvertimeBaseNet',
  HOURLY_RATE: 'ozAverageHourly',
  MONTHLY_AVG: 'ozAverageMonthly',
};

function Row({ label, children }) {
  return (
    <div className="flex items-center gap-2.5 border-b border-zinc-100 py-2 text-sm">
      <span className="w-[150px] shrink-0 truncate whitespace-nowrap text-[13px] text-zinc-500">
        {label}
      </span>
      <span className="min-w-0 flex-1 font-semibold">{children ?? '—'}</span>
    </div>
  );
}

/**
 * Read-only payroll (OZ) settings of one company. "Edit" opens the OZ page on
 * the settings tab of this company. Admin roles only (the caller checks
 * OZ.VIEW) — the company counterpart of the employee's OzProfileModal.
 */
export default function CompanyOzProfileModal({
  open,
  onClose,
  companyId,
  companyName,
}) {
  const { t } = useTranslation(['translations', 'forms']);
  const tp = (key, opts) =>
    t(`employeeProfile.${key}`, { ns: 'translations', ...opts });
  const tc = (key, opts) =>
    t(`companyProfile.${key}`, { ns: 'translations', ...opts });
  const label = (value) =>
    VALUE_LABELS[value] ? t(VALUE_LABELS[value], { ns: 'forms' }) : value;

  const { data: config, isLoading } = useCompanyConfig(open ? companyId : null);

  const editUrl = `${ROUTES.admin}/${ROUTES.oz}?tab=config&company=${companyId}`;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={760}
      closable={false}
      classNames={{ content: '!p-0 overflow-hidden !rounded-[18px]' }}
      destroyOnHidden
    >
      <div className="flex items-center justify-between gap-3 bg-[#17242A] px-5 py-4 text-white">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-extrabold">
              {tc('linkOzProfile')} — {companyName}
            </span>
            <span className="rounded-full bg-[#FFCC00] px-2.5 py-0.5 text-xs font-semibold text-zinc-900">
              {tp('ozAdminOnly')}
            </span>
          </div>
          <div className="mt-0.5 text-[13px] opacity-85">
            {tp('ozSubtitle')}
          </div>
        </div>
        <button
          type="button"
          aria-label={tp('close')}
          onClick={onClose}
          className="h-8 w-8 cursor-pointer rounded-full border-none bg-white/20 text-white"
        >
          ×
        </button>
      </div>

      {isLoading ? (
        <ModalRowsSkeleton rows={10} />
      ) : !config ? (
        <div className="px-5 py-6 text-sm text-zinc-500">
          {tc('ozNoConfig')}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-x-6 px-5 py-4">
          <Row label={t('ozCalcDirection', { ns: 'forms' })}>
            {label(config.calcDirection)}
          </Row>
          <Row label={t('ozPointValue', { ns: 'forms' })}>
            {config.pointValue?.amount != null
              ? `${config.pointValue.amount} ${config.pointValue.currency || ''}`.trim()
              : null}
          </Row>
          <Row label={t('ozPointValueSource', { ns: 'forms' })}>
            {label(config.pointValue?.source)}
          </Row>
          <Row label={t('ozWorkingHours', { ns: 'forms' })}>
            {label(config.workingHoursSource)}
          </Row>
          <Row label={t('ozRoundingSalary', { ns: 'forms' })}>
            {label(config.rounding?.salary)}
          </Row>
          <Row label={t('ozRoundingDeductions', { ns: 'forms' })}>
            {label(config.rounding?.deductions)}
          </Row>
          <Row label={t('ozDeductionOrder', { ns: 'forms' })}>
            {label(config.deductionOrder)}
          </Row>
          <Row label={t('ozOvertimeBase', { ns: 'forms' })}>
            {label(config.overtimeBase)}
          </Row>
          <Row label={t('ozAverageCalcMethod', { ns: 'forms' })}>
            {label(config.averageCalcMethod)}
          </Row>
          <Row label={t('effectiveFrom', { ns: 'forms' })}>
            {config.effectiveFrom
              ? dayjs(config.effectiveFrom).format(DATE_FORMATS.DISPLAY)
              : null}
          </Row>
          <Row label={tc('ozEarningCodes')}>
            {(config.earningCodes || []).length}
          </Row>
          <Row label={tc('ozDeductionTypes')}>
            {(config.deductionTypes || []).length}
          </Row>
        </div>
      )}

      <div className="flex items-center gap-1.5 whitespace-nowrap px-5 pt-3 text-xs text-zinc-500">
        <InfoCircleOutlined />
        <span className="truncate">{tc('infoOzCompany')}</span>
      </div>
      <div className="flex justify-end gap-2 px-5 pb-5 pt-3">
        <Button className="btn-default btn-default--hover" onClick={onClose}>
          {tp('close')}
        </Button>
        <Link to={editUrl}>
          <Button type="primary" className="btn-primary">
            {tp('ozEdit')} →
          </Button>
        </Link>
      </div>
    </Modal>
  );
}
